import { zodResolver } from '@hookform/resolvers/zod';
import { useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Switch,
  toast,
} from '@kmgestao/ui';

import {
  createLeadInputSchema,
  normalizeLeadInput,
  runLeadPrecheck,
  useCreateLead,
  useSendOnboardingLink,
  type CreateLeadInput,
  type DuplicateLead,
  type ExistingClinic,
} from '../../../features/leads/index.js';
import { useDefaultOnboardingTemplate } from '../../../features/onboarding/index.js';

import { AlreadyClientDialog } from './AlreadyClientDialog.js';
import { DuplicateLeadDialog } from './DuplicateLeadDialog.js';
import { LeadFormFields } from './LeadFormFields.js';

export interface NewLeadDialogProps {
  /** Trigger custom. Default: `<Button>Novo lead</Button>`. */
  trigger?: ReactNode;
  onCreated?: (leadId: string) => void;
}

/**
 * Dialog de criar lead — usa `LeadFormFields` (compartilhado com
 * `EditLeadDialog`) + hook de criação.
 *
 * **Auto-envio do questionário** (PR-M1-01b-i): o switch "Enviar
 * questionário de onboarding agora" vem ligado por default.
 * Ao submeter o form:
 *
 *   1. Cria o lead (`useCreateLead`).
 *   2. Se o switch está ON e há template publicado: dispara
 *      `useSendOnboardingLink` após o sucesso do create.
 *   3. Toast separado pro envio — **falha no envio NÃO desfaz o lead
 *      criado**; Karla reenvia manualmente pelo botão da página do
 *      detalhe.
 *
 * Se não houver template publicado (ambiente sem seed, ou template
 * despublicado), o switch fica desabilitado e o dialog mostra hint
 * explicando.
 */
export function NewLeadDialog({ trigger, onCreated }: NewLeadDialogProps) {
  const [open, setOpen] = useState(false);
  const createLead = useCreateLead();
  const sendOnboardingLink = useSendOnboardingLink();
  const defaultTemplate = useDefaultOnboardingTemplate();

  // Estado local (não vai para o Zod do lead — é só intent de UI).
  // Default true: caso comum é envio imediato. Karla desmarca só em
  // cadastro em massa ou sem email válido. Ver debate de produto em
  // PR-M1-01b-i.
  const [sendOnCreate, setSendOnCreate] = useState(true);

  // Estado dos dialogs de duplicata — detectados pelo precheck antes
  // do INSERT. `null` = fechado; objeto populado = abrir dialog
  // correspondente. Ver PR-M1-01d-0.6 e `precheck.ts`.
  const [duplicateLead, setDuplicateLead] = useState<DuplicateLead | null>(null);
  const [existingClient, setExistingClient] = useState<ExistingClinic | null>(null);
  const [prechecking, setPrechecking] = useState(false);

  const form = useForm<CreateLeadInput>({
    resolver: zodResolver(createLeadInputSchema),
    defaultValues: {
      full_name: '',
      legal_name: null,
      clinic_name_guess: null,
      email: '',
      phone: null,
      cnpj: null,
      address: {},
      specialty: null,
      source: 'manual',
      notes: null,
    },
  });

  const { handleSubmit, reset, formState } = form;

  const templateReady =
    !defaultTemplate.isLoading &&
    defaultTemplate.data !== null &&
    defaultTemplate.data !== undefined;
  const switchEnabled = templateReady && !formState.isSubmitting;

  const onSubmit = handleSubmit(async (raw) => {
    // Rede de segurança final: normaliza ANTES de persistir, mesmo
    // que o user tenha burlado o onBlur (ex: submeteu via Enter com
    // campo focado). Regra do sistema (ver formatters.ts::normalizeLeadInput).
    const values = normalizeLeadInput(raw);

    // Precheck de duplicata (PR-M1-01d-0.6). Antes de gastar INSERT,
    // verifica se CNPJ já é cliente ativo OU se email já é lead ativo.
    // Os dialogs correspondentes tomam o lugar do submit normal.
    setPrechecking(true);
    try {
      const precheck = await runLeadPrecheck({
        email: values.email,
        cnpj: values.cnpj,
      });
      if (precheck.kind === 'already_client') {
        setExistingClient(precheck.clinic);
        return; // cancela o INSERT — dialog informa que é cliente
      }
      if (precheck.kind === 'duplicate_lead') {
        setDuplicateLead(precheck.lead);
        return; // cancela o INSERT — dialog oferece abrir/reenviar
      }
    } finally {
      setPrechecking(false);
    }

    await createLead.mutateAsync(values, {
      onSuccess: async (lead) => {
        toast.success(`Lead "${lead.full_name}" criado.`);
        reset();
        setOpen(false);
        onCreated?.(lead.id);

        // Auto-envio do questionário — best-effort. Falha aqui NÃO
        // desfaz o lead; Karla reenvia depois.
        if (sendOnCreate && defaultTemplate.data) {
          try {
            const result = await sendOnboardingLink.mutateAsync({
              lead_id: lead.id,
              template_id: defaultTemplate.data.id,
            });
            if (result.email_status === 'sent') {
              toast.success(`Questionário enviado para ${lead.email}.`);
            } else if (result.email_status === 'skipped') {
              toast.info('Questionário preparado, mas email não foi enviado (modo dev).');
            } else {
              toast.warning(
                'Questionário preparado, mas email falhou. Reenvie pelo detalhe do lead.',
              );
            }
          } catch (err) {
            toast.error('Falha ao enviar questionário', {
              description: (err as Error).message + ' — reenvie pelo detalhe do lead.',
            });
          }
        }
      },
      onError: (err) => toast.error('Falha ao criar lead', { description: (err as Error).message }),
    });
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <DialogTrigger asChild>{trigger ?? <Button size="lg">Novo lead</Button>}</DialogTrigger>

      <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Novo lead</DialogTitle>
          <DialogDescription>
            Preencha o CNPJ para puxar dados da Receita. Todos os campos abaixo podem ser editados
            manualmente se a Receita estiver desatualizada ou se você ainda não tem o CNPJ.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} noValidate>
          <LeadFormFields form={form} idPrefix="new-lead" />

          {/* Auto-envio do questionário — Switch sempre visível, com
              hint dinâmico. Tap target grande (h-6 do trilho + padding
              do container) respeitando 44×44 em mobile. Layout em
              linha no >= sm, empilhado no mobile. */}
          <section className="mt-6 rounded-lg border border-border bg-muted/30 p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex-1 space-y-1">
                <p className="text-sm font-medium text-foreground">
                  Enviar questionário de onboarding agora
                </p>
                <p className="text-xs text-muted-foreground">
                  {templateReady
                    ? 'Após criar o lead, enviamos o link por email para ele começar a responder. Desative para cadastros em lote ou quando o email ainda não estiver confirmado.'
                    : defaultTemplate.isLoading
                      ? 'Carregando template padrão…'
                      : 'Nenhum template de onboarding publicado. Publique um em Configurações → Onboarding para ativar o envio automático.'}
                </p>
              </div>
              <div className="flex items-center gap-3 sm:shrink-0">
                <span
                  aria-hidden="true"
                  className="text-xs font-medium text-muted-foreground sm:hidden"
                >
                  {sendOnCreate ? 'Ativo' : 'Desativado'}
                </span>
                <Switch
                  checked={sendOnCreate && switchEnabled}
                  onCheckedChange={setSendOnCreate}
                  disabled={!switchEnabled}
                  srLabel="Enviar questionário de onboarding ao criar o lead"
                />
              </div>
            </div>
          </section>

          <DialogFooter className="mt-6 gap-2">
            <DialogClose asChild>
              <Button type="button" variant="outline" size="lg">
                Cancelar
              </Button>
            </DialogClose>
            <Button type="submit" size="lg" disabled={formState.isSubmitting || prechecking}>
              {prechecking ? 'Verificando…' : formState.isSubmitting ? 'Salvando…' : 'Criar lead'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>

      {/* Dialog overlays para duplicatas — ficam no mesmo componente
          para compartilhar estado do form (ex: user cancela no
          DuplicateLeadDialog e volta para o form com dados
          preservados). */}
      {existingClient ? (
        <AlreadyClientDialog
          open={existingClient !== null}
          onOpenChange={(next) => {
            if (!next) setExistingClient(null);
          }}
          clinic={existingClient}
        />
      ) : null}
      {duplicateLead ? (
        <DuplicateLeadDialog
          open={duplicateLead !== null}
          onOpenChange={(next) => {
            if (!next) setDuplicateLead(null);
          }}
          lead={duplicateLead}
        />
      ) : null}
    </Dialog>
  );
}
